import { classNames as classnames } from "@circle-react/helpers/twMergeWithCN";
import type { Space } from "@circle-react/types/Space";
import { find } from "lodash";
import type { ComponentType } from "react";

import { t } from "@/i18n-js/instance";
import { anchorClassNames } from "@/react/helpers/cssAnchorHelpers";

import { useDropdown } from "../DropdownMenu";
import { SpacesDropdown } from "./SpacesDropdown";
import type { SpaceSelectButtonProps } from "./SpaceSelectButton";
import { SpaceSelectButton } from "./SpaceSelectButton";

export interface SpaceSelectComponentProps {
  containerClassname?: string;
  customButton?: ComponentType<SpaceSelectButtonProps>;
  defaultButtonLabel?: string;
  disabled?: boolean;
  dropdownClassName?: string;
  onChange?: (id: number | null) => void;
  placeholder?: string;
  shouldDisplayHomepage?: boolean;
  shouldShowEmoji?: boolean;
  spaces?: Space[];
  value?: number | null;
  variant?: string;
}

export const SpaceSelectComponent = ({
  spaces,
  onChange,
  value,
  variant,
  containerClassname,
  dropdownClassName,
  shouldShowEmoji,
  disabled,
  defaultButtonLabel = t("select_space"),
  placeholder = t("search_space_placeholder"),
  customButton,
  shouldDisplayHomepage,
}: SpaceSelectComponentProps) => {
  const { open: isOpen, toggleDropdown, handleClose } = useDropdown(false);
  const selectedSpace = value != null ? find(spaces, { id: value }) : undefined;
  const containerClassnames = classnames(
    anchorClassNames("react-space-selector", {
      "form-control": variant === "form-input",
    }),
    containerClassname,
  );

  const SpaceSelectButtonComponent = customButton || SpaceSelectButton;

  return (
    <div className={containerClassnames} data-testid="space-selector">
      <SpaceSelectButtonComponent
        selectedSpace={selectedSpace}
        toggleDropdown={disabled ? undefined : toggleDropdown}
        shouldShowEmoji={shouldShowEmoji}
        defaultLabel={defaultButtonLabel}
        disabled={disabled}
        variant={variant}
        shouldDisplayHomepage={shouldDisplayHomepage}
        value={value ?? undefined}
      />
      <SpacesDropdown
        placeholder={placeholder}
        handleClose={handleClose}
        open={isOpen}
        onChange={onChange}
        spaces={spaces}
        className={dropdownClassName}
        shouldDisplayHomepage={shouldDisplayHomepage}
      />
    </div>
  );
};
